import {
  Badge,
  Button,
  Card,
  EmptyState,
  Modal,
  ModalProps,
  PaginationBar,
  UserBadge,
} from '@payaca/ambrosia';
import { FC, useState } from 'react';

import useGetMyRegionPreferences from '@/api/queries/me/useGetMyRegionPreferences';
import useGetProjectCustomFieldAuditLogs, {
  ActingUser,
  ProjectCustomFieldAuditLog,
} from '@/api/queries/project/useGetProjectCustomFieldAuditLogs';
import { usePaginationState } from '@/ui/pages/projectPage/components/MaterialsPanel/materialsPanelHooks';

import { CustomFieldHelperType, CustomFieldType } from '@payaca/custom-fields';
import {
  DateFormats,
  getInternationalDateFormatByLocale,
} from '@payaca/helpers/internationalHelper';
import { CustomFieldGroupRole } from '@payaca/types/custom-fields';
import { Nullish } from '@payaca/utilities/types';
import { format } from 'date-fns';
import useGetCustomerCustomFieldAuditLogs from '../../../api/queries/customers/useGetCustomerCustomFieldAuditLogs';
import { CustomFieldGroupProps } from './CustomFieldGroup';

export const CustomFieldValueAuditLogsModal: FC<
  Omit<ModalProps, 'title'> & {
    fieldIdentifier: string;
    type: CustomFieldGroupRole;
  } & CustomFieldGroupProps
> = (props) => {
  const { fieldIdentifier, onClose, type, ...rest } = props;

  const { pagination, setCurrentPage } = usePaginationState(50);

  const { auditLogs, field, data, isLoading } =
    useGetProjectCustomFieldAuditLogs(
      type === 'project' ? props.projectId : props.customerId,
      fieldIdentifier,
      pagination
    );

  const { data: accountRegionPreferences } = useGetMyRegionPreferences();

  return (
    <Modal
      title={`Audit logs for "${field?.label}"`}
      onClose={onClose}
      {...rest}
    >
      {auditLogs && (
        <>
          <Modal.Body>
            <div className="space-y-4">
              {!isLoading && !auditLogs?.items?.length && <EmptyState />}
              {auditLogs?.items?.map((item, i) => {
                return (
                  <Card key={i}>
                    <Card.Body className="space-y-4">
                      <div className="flex w-full flex-col-reverse gap-1.5 bg-transparent md:flex-row md:gap-2">
                        <Source source={item.metadata?.source} />
                        <div className="ml-auto supporting-body">
                          {format(
                            item.timestamp,
                            getInternationalDateFormatByLocale(
                              DateFormats.TIMESTAMP_SHORT,
                              accountRegionPreferences?.locale
                            )
                          )}
                        </div>
                      </div>
                      <div>
                        <Value field={field} value={item.value} />
                      </div>
                      <ActingUserBadge actingUser={item.actingUser} />
                    </Card.Body>
                  </Card>
                );
              })}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <PaginationBar
              pageSize={auditLogs.limit}
              currentPage={auditLogs.offset / auditLogs.limit + 1}
              totalItems={auditLogs.totalCount}
              onPageChange={setCurrentPage}
            />
          </Modal.Footer>
        </>
      )}
    </Modal>
  );
};

const ActingUserBadge: FC<{ actingUser: ActingUser }> = ({ actingUser }) => {
  if (!actingUser) return null;

  switch (actingUser.__typename) {
    case 'User':
      return (
        <UserBadge
          user={{
            firstName: actingUser.firstName,
            lastName: actingUser.lastName,
            imgSrc: actingUser.avatarUrl || undefined,
            userColour: actingUser.colour || undefined,
            emailAddress: actingUser.email || '',
          }}
        />
      );
    case 'MissingUser':
      // This is the case when the user who made the change does not belong to your account (ie franchisor admin; payaca admin)
      return (
        <Badge colour="gray" variant="soft">
          Missing user
        </Badge>
      );
    default:
      return null;
  }
};

const Source: FC<{
  source: NonNullable<ProjectCustomFieldAuditLog['metadata']>['source'];
}> = ({ source }) => {
  if (!source) return null;

  return (
    <span>
      {(() => {
        switch (source.type) {
          case 'TASK_SUBMISSION':
            return 'Task submission';
          case 'SERVICE_ACCOUNT_API':
            return 'External service account API';
          default:
            return null;
        }
      })()}
    </span>
  );
};

const Value: FC<{
  value?: ProjectCustomFieldAuditLog['value'];
  field: Nullish<CustomFieldHelperType[CustomFieldType]['definition']>;
}> = ({ value, field }) => {
  const parsedValue = value ? JSON.parse(value) : null;
  const isValid = field?.typecheckValue(parsedValue);

  if (!value) {
    return <span className="text-gray-500 italic">No value</span>;
  }

  if (field?.type === 'url' || field?.type === 'fieldset' || !isValid) {
    return <JsonValue value={parsedValue} />;
  }

  return <span>{field?.formatValue(parsedValue)}</span>;
};

const JsonValue: FC<{
  value: any;
}> = ({ value }) => {
  const [showMore, setShowMore] = useState(false);

  return (
    <div>
      <pre
        className={`rounded-md bg-gray-100 p-2 text-sm ${
          showMore
            ? ''
            : 'container-bottom-shadow relative max-h-[60px] overflow-hidden'
        }`}
      >
        {JSON.stringify(value, undefined, 2)}
      </pre>
      <Button
        onClick={(e) => {
          e.stopPropagation();
          setShowMore((s) => !s);
        }}
        variant={'link-inline'}
        size={'sm'}
      >
        {showMore ? 'Show less' : 'Show more'}
      </Button>
    </div>
  );
};
